import { useIntl } from "react-intl";
import { Link } from "react-router";
import type { TobisFavorite } from "@/lib/tobis-favorites";
import { useTobisFavorites } from "@/lib/use-tobis-favorites";
import { TobisTitleCover } from "./TobisTitleCard";

// Die Merkliste als Zeile auf der Startseite: markierte Titel UND Personen in
// einer gemeinsamen Zeile, neueste Markierung zuerst.
//
// Eine Zeile fuer beide Arten und in derselben Kachelform: TMDB-Portraits sind
// wie Poster im Verhaeltnis 2:3, die Zeile bleibt damit ein ruhiger Streifen
// statt zweier Bloecke mit unterschiedlichem Raster. Unterschieden wird ueber
// die Unterzeile (Jahr beim Titel, Rollenzahl bei der Person) - ein zusaetzliches
// Typ-Abzeichen waere Farbe und Flaeche fuer eine Information, die schon dasteht.
//
// Kein Stern auf der Kachel: der Schalter lebt auf der Detailseite, wo auch
// alles andere zu diesem Datensatz steht. Zwei Orte fuer denselben Schalter
// waeren zwei Orte, an denen er kaputtgehen kann.

function FavoriteCard({ favorite, index }: { favorite: TobisFavorite; index: number }) {
  const intl = useIntl();
  const to =
    favorite.kind === "title"
      ? `/tobis/titles/${favorite.refId}`
      : `/tobis/people/${favorite.refId}`;
  const meta =
    favorite.kind === "title"
      ? favorite.year === null
        ? null
        : String(favorite.year)
      : intl.formatMessage(
          { id: "tobis.people.credits", defaultMessage: "{count} roles" },
          { count: favorite.creditCount ?? 0 },
        );

  return (
    <Link to={to} className="group w-28 shrink-0 focus:outline-none @sm:w-32">
      <div className="box relative aspect-[2/3] overflow-hidden rounded-md bg-surface transition group-hover:ring-accent/40 group-focus-visible:ring-2 group-focus-visible:ring-ink/30">
        {/* Geteilt mit der Titelkachel, weil der Platzhalter fuer "kein Bild"
            nicht zweimal geschrieben werden soll - fuer Personen ohne Portrait
            gilt derselbe Fall. */}
        <TobisTitleCover title={{ coverUrl: favorite.imageUrl }} index={index} />
      </div>
      <div className="mt-1.5 line-clamp-2 text-[12px] leading-tight text-ink/80 transition group-hover:text-accent">
        {favorite.name}
      </div>
      {meta ? <div className="text-[11px] text-ink/40">{meta}</div> : null}
    </Link>
  );
}

export function TobisFavoritesRow() {
  const intl = useIntl();
  const { favorites } = useTobisFavorites();

  // Solange nichts geladen ist, bleibt die Zeile weg statt einen Platzhalter zu
  // zeigen: sie steht unter den Kennzahlen, ein Aufblitzen wuerde die Seite
  // beim Laden zweimal umbauen.
  if (favorites === null) return null;

  return (
    <div>
      <div className="mb-2 text-[12px] text-ink/45">
        {intl.formatMessage({ id: "home.tobis.favorites", defaultMessage: "Favourites" })}
      </div>
      {favorites.length === 0 ? (
        // Der leere Zustand erklaert den Weg dorthin - ohne diesen Satz waere
        // die Merkliste ein Feature, das man nur findet, wenn man es kennt.
        <p className="text-[12px] text-ink/45">
          {intl.formatMessage({
            id: "home.tobis.favoritesEmpty",
            defaultMessage: "No favourites yet. Use the star on a title or person page.",
          })}
        </p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-1">
          {favorites.map((f, i) => (
            <FavoriteCard key={`${f.kind}:${f.refId}`} favorite={f} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
